import { useEffect, useState } from "react";
import { ActivityIndicator, TextInput, View } from "react-native";
import {
  Dialog,
  DialogHeader,
  DialogPrimaryButton,
  DialogSecondaryButton,
  MoonFieldInput,
  MoonFormField,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import { EMBER } from "../../../../onboarding/theme";
import type { MonitoringEntryDraft } from "../suggested/MonitoringEntryModal";

export type MonitoringCellKey = "follicles" | "lining" | "e2" | "lh" | "p4";

export function isMonitoringCellKey(value: string): value is MonitoringCellKey {
  return ["follicles", "lining", "e2", "lh", "p4"].includes(value);
}

type SimpleCellConfig = {
  draftKey: "liningThickness" | "estradiol" | "lh" | "progesterone";
  titleKey: string;
  fieldLabelKey: string;
  unit: string;
  unitKey?: "estradiolUnit" | "lhUnit" | "progesteroneUnit";
  placeholderKey: string;
  max: number;
};

const SIMPLE_CELL_CONFIG: Record<
  Exclude<MonitoringCellKey, "follicles">,
  SimpleCellConfig
> = {
  lining: {
    draftKey: "liningThickness",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lining_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lining_label",
    unit: "mm",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.lining_placeholder",
    max: 50,
  },
  e2: {
    draftKey: "estradiol",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.estradiol_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.estradiol_label",
    unit: "pg/mL",
    unitKey: "estradiolUnit",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 10000,
  },
  lh: {
    draftKey: "lh",
    titleKey: "care.overview.calendar.monitoring_log.modal.cell_edit.lh_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lh_label",
    unit: "mIU/mL",
    unitKey: "lhUnit",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 100,
  },
  p4: {
    draftKey: "progesterone",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.progesterone_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.progesterone_label",
    unit: "ng/mL",
    unitKey: "progesteroneUnit",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 50,
  },
};

function parseSizeList(value: string): number[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number)
    .filter((item) => Number.isFinite(item));
}

function sizeListError(value: string, label: string): string | null {
  if (!value.trim()) return null;
  const tokens = value.split(",").map((item) => item.trim());
  if (tokens.some((item) => !item || !/^\d+(\.\d+)?$/.test(item))) {
    return `${label} must use comma-separated numbers.`;
  }
  if (parseSizeList(value).some((item) => item < 0 || item > 50)) {
    return `${label} must be between 0 and 50 mm.`;
  }
  return null;
}

function numberError(value: string, label: string, max: number): string | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > max) {
    return `${label} must be between 0 and ${max}.`;
  }
  return null;
}

function derivedCountAfterSizeEdit(
  previousSizes: string,
  previousCount: string,
  nextSizes: string,
): string {
  if (nextSizes.trim()) return String(parseSizeList(nextSizes).length);
  if (
    previousSizes.trim() &&
    previousCount === String(parseSizeList(previousSizes).length)
  ) {
    return "";
  }
  return previousCount;
}

function formatMonitoringDate(dateISO: string, locale: Locale): string {
  const date = new Date(`${dateISO}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateISO;
  return formatDate(date, locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function FocusedInput({
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  accessibilityLabel: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <MoonFieldInput focused={focused}>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        accessibilityLabel={accessibilityLabel}
        keyboardType="decimal-pad"
        style={{
          minHeight: 44,
          paddingVertical: 10,
          color: EMBER.colors.text,
          fontSize: 14,
        }}
      />
    </MoonFieldInput>
  );
}

export function MonitoringCellDetailDialog({
  visible,
  dateISO,
  cellKey,
  current,
  onClose,
  onSave,
}: {
  visible: boolean;
  dateISO: string;
  cellKey: MonitoringCellKey | null;
  current: MonitoringEntryDraft;
  onClose: () => void;
  onSave: (patch: Partial<MonitoringEntryDraft>) => Promise<void>;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [value, setValue] = useState("");
  const [secondaryValue, setSecondaryValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible || !cellKey) return;
    if (cellKey === "follicles") {
      setValue(current.leftSizes);
      setSecondaryValue(current.rightSizes);
    } else {
      setValue(current[SIMPLE_CELL_CONFIG[cellKey].draftKey]);
      setSecondaryValue("");
    }
    setError(null);
  }, [cellKey, current, visible]);

  if (!visible || !cellKey) return null;

  const dateLabel = formatMonitoringDate(dateISO, locale);
  const isFollicleCell = cellKey === "follicles";
  const config = isFollicleCell ? null : SIMPLE_CELL_CONFIG[cellKey];
  const unit = config?.unitKey
    ? current[config.unitKey] || config.unit
    : config?.unit;

  const handleSave = async () => {
    let validationError: string | null = null;
    if (isFollicleCell) {
      const leftError = sizeListError(
        value,
        t("care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary"),
      );
      const rightError = sizeListError(
        secondaryValue,
        t("care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary"),
      );
      validationError = leftError ?? rightError;
      if (!validationError && !value.trim() && !secondaryValue.trim()) {
        validationError = t(
          "care.overview.calendar.monitoring_log.modal.cell_edit.value_required",
        );
      }
    } else if (config) {
      validationError = numberError(value, t(config.fieldLabelKey), config.max);
      if (!validationError && !value.trim()) {
        validationError = t(
          "care.overview.calendar.monitoring_log.modal.cell_edit.value_required",
        );
      }
    }
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isFollicleCell) {
        await onSave({
          leftSizes: value,
          rightSizes: secondaryValue,
          leftCount: derivedCountAfterSizeEdit(
            current.leftSizes,
            current.leftCount,
            value,
          ),
          rightCount: derivedCountAfterSizeEdit(
            current.rightSizes,
            current.rightCount,
            secondaryValue,
          ),
        });
      } else if (config) {
        await onSave({ [config.draftKey]: value });
      }
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : t(
              "care.overview.calendar.monitoring_log.modal.cell_edit.save_error",
            ),
      );
    } finally {
      setSaving(false);
    }
  };

  const title = isFollicleCell
    ? t("care.overview.calendar.monitoring_log.modal.cell_edit.follicles_title")
    : t(config!.titleKey);
  const subtitle = t(
    "care.overview.calendar.monitoring_log.modal.cell_edit.subtitle",
    { params: { date: dateLabel } },
  );

  return (
    <Dialog
      visible={visible}
      onClose={saving ? () => undefined : onClose}
      closeButton="minimal"
      testID="monitoring-cell-dialog"
      containerStyle={{ width: "90%", maxWidth: 390, maxHeight: 560 }}
      surfaceStyle={{ maxHeight: 560 }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: 560,
      }}
    >
      <DialogHeader
        title={title}
        subtitle={subtitle}
        style={{ marginBottom: 16 }}
      />
      <View style={{ gap: 12 }}>
        {isFollicleCell ? (
          <>
            <MoonFormField
              label={t(
                "care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary",
              )}
              labelHint="mm · comma separated"
            >
              <FocusedInput
                value={value}
                onChangeText={setValue}
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary",
                )}
              />
            </MoonFormField>
            <MoonFormField
              label={t(
                "care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary",
              )}
              labelHint="mm · comma separated"
            >
              <FocusedInput
                value={secondaryValue}
                onChangeText={setSecondaryValue}
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary",
                )}
              />
            </MoonFormField>
          </>
        ) : (
          <MoonFormField label={t(config!.fieldLabelKey)} labelHint={unit}>
            <FocusedInput
              value={value}
              onChangeText={setValue}
              placeholder={t(config!.placeholderKey)}
              accessibilityLabel={t(config!.fieldLabelKey)}
            />
          </MoonFormField>
        )}
        {error ? (
          <Text
            accessibilityRole="alert"
            style={{ color: "#B42318", fontSize: 12, lineHeight: 17 }}
          >
            {error}
          </Text>
        ) : null}
      </View>
      <View style={{ flexDirection: "row", gap: 8, marginTop: 20 }}>
        <DialogSecondaryButton
          flex={1}
          label={t(
            "care.overview.calendar.monitoring_log.modal.cell_edit.cancel",
          )}
          onPress={onClose}
          disabled={saving}
        />
        <DialogPrimaryButton
          flex={1.35}
          label={t(
            "care.overview.calendar.monitoring_log.modal.cell_edit.save",
          )}
          onPress={() => void handleSave()}
          disabled={saving}
          icon={
            saving ? (
              <ActivityIndicator color={EMBER.colors.textLight} size="small" />
            ) : undefined
          }
        />
      </View>
    </Dialog>
  );
}
